import { Plan } from 'Features/pricing/types/Plan'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ProductTypes } from 'Types/ProductTypes'
import { CurrenciesTypes } from 'Types/Currencies'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'
import { useAddSubscription } from 'Features/checkout/api/addSubscription'
import { useFrontEndEventTrackingService } from '@clickcease/cc-client-components-lib'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'

type useSelectPlanProps = {
  numOfVisits: number
  type: string
  reactivation?: boolean
  billingCycleMonths?: number
}

export const useSelectPlan = (
  plans: Array<Plan>,
  product: ProductTypes,
  currency: CurrenciesTypes
) => {
  const navigate = useNavigate()
  const { t } = useTranslation('pricing')
  const shouldSkipCheckout = usePaymentsFlowSplit()
  const { track } = useFrontEndEventTrackingService()
  const { mutate: addSubscription, isLoading } = useAddSubscription()
  const { forceNoCc } = useUserStore()
  const pricingEndOfTrial = usePricingEndOfTrial()

  const planSelected = ({
    type,
    numOfVisits,
    reactivation,
    billingCycleMonths,
  }: useSelectPlanProps) => {
    const plan = plans.find((plan) => {
      return (
        plan.visitsAllowed === numOfVisits * 1000 &&
        plan.planType === type &&
        plan.billingCycleMonths === (billingCycleMonths || 12) &&
        plan.description.includes(product)
      )
    })

    if (shouldSkipCheckout === 'NO_CC' && !pricingEndOfTrial) {
      createNoCreditCardSubscription(plan)
    } else {
      goToCheckout(plan, type, numOfVisits, reactivation, billingCycleMonths)
    }
  }

  const createNoCreditCardSubscription = (plan: Plan | undefined) => {
    addSubscription(
      { packageIds: [plan?.id || 0], forceNoCc },
      {
        onSuccess: () => {
          track(['segment'], `Subscription created with no credit card`, {
            amount: plan?.amount,
            currency: plan?.currency,
            planType: plan?.planType,
            product: plan?.productType,
            visits: plan?.visitsAllowed,
          })
          setTimeout(() => {
            navigate('/onboarding')
          }, 1500)
        },
        onError: () => {
          console.log('error adding subscription')
        },
      }
    )
  }

  const goToCheckout = (
    plan: Plan | undefined,
    type: string,
    numOfVisits: number,
    reactivation = false,
    billingCycleMonths = 12
  ) => {
    navigate('/checkout', {
      state: {
        reactivation,
        billingCycleMonths,
        planName: t(`${product}_plan_${type.toLowerCase()}`),
        planType: type,
        planId: plan?.id,
        allowedVisits: 1000 * numOfVisits,
        currency,
        amount: plan?.amount,
        pricingId: plan?.pricingId,
      },
    })
  }

  return { planSelected, isLoading, goToCheckout }
}
